import { observable, runInAction } from 'mobx';
import { PriceEntity } from 'Models/Entities';
import * as Enums from 'Models/Enums';

// UI-only registry of cells the user has put into "Fixed, no amount yet" state.
// The model can't tell this apart from INHERIT (both are amount=null,
// mode='FIXED'), so PriceCell's per-cell pendingKind normally papers over it.
// The bulk "Fixed" tool mutates the shared model and can't touch that React
// state, so it marks the entities here instead; PriceCell (a MobX observer)
// reads the mark to show the Fixed selector with an empty input. Any explicit
// writeCell supersedes the hint and clears it (see writeCell), so the mark only
// lives until the cell is next edited. Nothing here is persisted.
const pendingFixedRegistry = observable.set<PriceEntity>();

export function markPendingFixed(price: PriceEntity): void {
	runInAction(() => pendingFixedRegistry.add(price));
}

export function clearPendingFixed(price: PriceEntity): void {
	runInAction(() => pendingFixedRegistry.delete(price));
}

export function isPendingFixed(price: PriceEntity): boolean {
	return pendingFixedRegistry.has(price);
}

// The UI distinguishes three cell states even though storage only has two
// priceMode values. INHERIT is a derived UI-only state that means "no
// override, use the trip standard." It maps to row absence in storage (amount
// = null, which the save filter drops; the canonical mode for an inherited
// cell is FIXED).
export type PriceCellState =
	| { kind: 'INHERIT' }
	| { kind: 'FIXED'; amount: number | undefined }
	| { kind: 'PERCENTAGE'; amount: number | undefined };

export type PriceCellKind = PriceCellState['kind'];

export const priceCellOptions: { [key in PriceCellKind]: string } = {
	INHERIT: 'Standard',
	FIXED: 'Fixed',
	PERCENTAGE: 'Percentage',
};

// Reads the cell state from a PriceEntity. The (amount=null, mode=FIXED) combo
// is the canonical "no override" representation — any other null-amount state
// means the user picked a mode but is mid-edit, and the UI should reflect that
// rather than silently snapping back to INHERIT.
export function readCell(price: PriceEntity): PriceCellState {
	const amount = price.amount as number | undefined | null;
	const mode = (price.mode ?? 'FIXED') as Enums.priceMode;

	if (mode === 'PERCENTAGE') {
		return { kind: 'PERCENTAGE', amount: amount ?? undefined };
	}
	if (amount == null) {
		return { kind: 'INHERIT' };
	}
	return { kind: 'FIXED', amount };
}

// Writes a cell state back to the entity. Wrapped in runInAction so callers
// don't need to remember to do it — every mutation here touches MobX-observed
// state.
export function writeCell(price: PriceEntity, next: PriceCellState): void {
	runInAction(() => {
		// An explicit write is the source of truth; the bulk "pending Fixed" hint
		// only applies until the cell is next touched. (The bulk tool re-marks
		// after writing, so its own writes still show Fixed with a blank input.)
		pendingFixedRegistry.delete(price);
		switch (next.kind) {
			case 'INHERIT':
				price.amount = undefined as unknown as number;
				price.mode = 'FIXED';
				break;
			case 'FIXED':
				price.amount = next.amount as unknown as number;
				price.mode = 'FIXED';
				break;
			case 'PERCENTAGE':
				price.amount = next.amount as unknown as number;
				price.mode = 'PERCENTAGE';
				break;
			default: {
				const _exhaustive: never = next;
				throw new Error(`Unhandled PriceCellState kind: ${JSON.stringify(_exhaustive)}`);
			}
		}
	});
}

// Compares two cell states for sync-banner equality. Two cells are in sync
// when they share a kind AND, for non-INHERIT kinds, the amount matches to
// the cent.
export function cellsEqual(a: PriceCellState, b: PriceCellState): boolean {
	if (a.kind !== b.kind) return false;
	if (a.kind === 'INHERIT') return true;
	const round = (n: number | undefined) => (n == null ? null : Math.round(n * 100) / 100);
	return round(a.amount) === round((b as { amount: number | undefined }).amount);
}

export function describeCell(state: PriceCellState): string {
	switch (state.kind) {
		case 'INHERIT':
			return 'Standard';
		case 'FIXED':
			return state.amount == null ? 'Fixed (—)' : `$${state.amount}`;
		case 'PERCENTAGE':
			return state.amount == null ? '%' : `${state.amount}%`;
		default: {
			const _exhaustive: never = state;
			return JSON.stringify(_exhaustive);
		}
	}
}
